import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  TruckIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { ServiceClient, ServiceCategory } from '@/types';
import { getServices, getServiceCategories, transformServiceToClient } from '@/services/api';
import { Vehicle, getVehicles } from '@/services/vehicles';
import HolidayCalendar from '@/components/HolidayCalendar';
import VehicleManager from '@/components/VehicleManager';
import { checkDateAvailability, checkTimeAvailability } from '@/services/quickBooking';
import { checkAvailability, AvailabilityResponse, getAvailableTimeSlots, TimeSlot, createAppointment } from '@/services/appointments';
import { getWorkingDaysConfig } from '@/services/holidays';
import { useClientAuthStore } from '@/store/useClientAuthStore';
import Notification from '@/components/Notification';

interface BookingData {
  services: ServiceClient[];
  date: string;
  time: string;
  vehicle: Vehicle | null;
  notes: string;
}

const QuickBookPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useClientAuthStore();
  
  // Verificar si hay un servicio preseleccionado
  const preselectedService = location.state?.selectedService as ServiceClient | null;
  
  const [currentStep, setCurrentStep] = useState(preselectedService ? 2 : 1);
  const [bookingData, setBookingData] = useState<BookingData>({
    services: preselectedService ? [preselectedService] : [],
    date: '',
    time: '',
    vehicle: null,
    notes: ''
  });

  // Progreso (para barra superior en móviles)
  const totalSteps = 5;
  const progressPercent = Math.round(((currentStep - 1) / (totalSteps - 1)) * 100);

  // Estados para datos
  const [services, setServices] = useState<ServiceClient[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estados para notificaciones
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    isVisible: boolean;
  }>({
    type: 'info',
    message: '',
    isVisible: false
  });

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Horarios dinámicos según configuración de trabajo
  const [workingHoursByDay, setWorkingHoursByDay] = useState<Record<number, { start: string; end: string } | null>>({});

  // Estados para slots disponibles
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);

  // Función para convertir formato 24h a 12h
  const formatTimeTo12Hour = (time24: string) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Cargar horarios disponibles cuando cambie la fecha
  useEffect(() => {
    if (bookingData.date) {
      loadAvailableTimeSlots();
    } else {
      setAvailableTimeSlots([]);
    }
  }, [bookingData.date]);

  // Cargar horarios disponibles para la fecha seleccionada
  const loadAvailableTimeSlots = async () => {
    if (!bookingData.date) return;

    setIsLoadingTimeSlots(true);
    try {
      const timeSlotsData = await getAvailableTimeSlots(bookingData.date);
      // Solo mostrar los horarios que están disponibles
      const availableSlots = timeSlotsData.availableSlots.filter(slot => slot.available);
      setAvailableTimeSlots(availableSlots);

      // Si el horario seleccionado ya no está disponible, limpiarlo
      if (bookingData.time && !availableSlots.some(slot => slot.time === bookingData.time)) {
        setBookingData(prev => ({ ...prev, time: '' }));
      }
    } catch (error) {
      console.error('Error loading available time slots:', error);
      setAvailableTimeSlots([]);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [servicesData, categoriesData, vehiclesData, workingConfig] = await Promise.all([
          getServices(),
          getServiceCategories(),
          getVehicles(),
          getWorkingDaysConfig()
        ]);

        const transformedServices = servicesData.map(transformServiceToClient);
        setServices(transformedServices);
        setCategories(categoriesData);
        setVehicles(vehiclesData);
        const hours: Record<number, { start: string; end: string } | null> = { 0: null, 1: null, 2: null, 3: null, 4: null, 5: null, 6: null };
        Object.entries((workingConfig as any).workingHours || {}).forEach(([k, v]) => {
          const idx = parseInt(k);
          hours[idx] = v as any;
        });
        setWorkingHoursByDay(hours);

        // Seleccionar vehículo por defecto
        const defaultVehicle = vehiclesData.find(v => v.isDefault);
        if (defaultVehicle) {
          setBookingData(prev => ({ ...prev, vehicle: defaultVehicle }));
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filtrar servicios
  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const matchesSearch = searchTerm === '' ||
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || String(service.category.id) === String(selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [services, searchTerm, selectedCategory]);

  // Manejar selección/deselección de servicio (multi-select)
  const handleServiceToggle = (service: ServiceClient) => {
    setBookingData(prev => {
      const isAlreadySelected = prev.services.some(s => s.id === service.id);
      const nextServices = isAlreadySelected
        ? prev.services.filter(s => s.id !== service.id)
        : [...prev.services, service];
      return { ...prev, services: nextServices };
    });
  };

  // Manejar selección de fecha
  const handleDateSelect = async (date: string) => {
    // Validar contra API (días laborables y festivos)
    const res = await checkDateAvailability(date);
    if (!res.available) {
      showNotification('error', res.reason || 'Fecha no disponible');
      return;
    }
    setBookingData(prev => ({ ...prev, date }));
  };

  // Manejar selección de hora
  const handleTimeSelect = async (time: string) => {
    if (!bookingData.date) return;
    const res = await checkTimeAvailability(bookingData.date, time);
    if (!res.available) {
      showNotification('error', res.reason || 'Horario no disponible');
      return;
    }
    setBookingData(prev => ({ ...prev, time }));
  };

  // Manejar selección de vehículo
  const handleVehicleSelect = (vehicle: Vehicle) => {
    setBookingData(prev => ({ ...prev, vehicle }));
  };

  // Manejar notas
  const handleNotesChange = (notes: string) => {
    setBookingData(prev => ({ ...prev, notes }));
  };

  // Función para mostrar notificaciones
  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({
      type,
      message,
      isVisible: true
    });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  // Validar si puede continuar
  const canProceedToStep2 = () => bookingData.services.length > 0;
  const canProceedToStep3 = () => bookingData.services.length > 0 && bookingData.vehicle;
  const canProceedToStep4 = () => bookingData.services.length > 0 && bookingData.vehicle && bookingData.date && bookingData.time;
  const canSubmit = () => bookingData.services.length > 0 && bookingData.date && bookingData.time && bookingData.vehicle;

  // Enviar reserva
  const handleSubmit = async () => {
    if (!canSubmit()) return;

    setIsSubmitting(true);
    try {
      console.log('🚀 QuickBook - Creating appointment with multiple services:', {
        serviceIds: bookingData.services.map(s => s.id),
        serviceCount: bookingData.services.length,
        date: bookingData.date,
        time: bookingData.time,
        vehicleId: bookingData.vehicle!.id
      });

      // Crear una sola cita con múltiples servicios
      // Formato básico para que el servidor maneje el timezone
      const cleanTime = bookingData.time.split(':').slice(0, 2).join(':');
      const appointmentDateTime = `${bookingData.date}T${cleanTime}:00`;

      await createAppointment({
        serviceIds: bookingData.services.map(service => parseInt(service.id)),
        appointmentDate: appointmentDateTime,
        notes: bookingData.notes.trim() || undefined,
        vehicleId: bookingData.vehicle!.id
      });

      setCurrentStep(5);
      showNotification('success', '¡Cita reservada exitosamente!');
    } catch (error) {
      console.error('Error creating appointment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al crear la reserva. Por favor intenta de nuevo.';
      showNotification('error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Renderizar indicador de pasos
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[
        { step: 1, label: 'Servicio' },
        { step: 2, label: 'Vehículo' },
        { step: 3, label: 'Fecha y Hora' },
        { step: 4, label: 'Confirmar' },
        { step: 5, label: 'Listo' }
      ].map(({ step, label }) => (
        <div key={step} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep >= step 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-300 text-gray-600'
          }`}>
            {step}
          </div>
          <span className={`ml-2 text-sm font-medium ${
            currentStep >= step ? 'text-blue-600' : 'text-gray-500'
          }`}>
            {label}
          </span>
          {step < 5 && (
            <div className={`w-16 h-0.5 mx-4 ${
              currentStep > step ? 'bg-blue-500' : 'bg-gray-300'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  // Paso 1: Selección de servicio
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">¿Qué servicio necesitas?</h2>
        <p className="text-gray-600">Selecciona el servicio que mejor se adapte a tu vehículo</p>
      </div>

      {/* Búsqueda rápida */}
      <div className="relative">
        <input
          type="text"
          placeholder="Buscar servicios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <WrenchScrewdriverIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
      </div>

      {/* Categorías */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            selectedCategory === 'all'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Todos
        </button>
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(String(category.id))}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === String(category.id)
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>


      {/* Lista de servicios */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Cargando servicios...</p>
          </div>
        ) : (
          filteredServices.map(service => (
            <div
              key={service.id}
              onClick={() => handleServiceToggle(service)}
              className={`p-4 border rounded-lg transition-all cursor-pointer ${
                bookingData.services.some(s => s.id === service.id)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <WrenchScrewdriverIcon className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{service.name}</h3>
                    <p className="text-sm text-gray-600">{service.shortDescription}</p>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-sm text-gray-500">
                        <ClockIcon className="w-4 h-4 inline mr-1" />
                        {service.estimatedTime}
                      </span>
                      <span className="text-sm text-gray-500">
                        {service.category.name}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-blue-600">${service.price}</div>
                  {bookingData.services.some(s => s.id === service.id) ? (
                    <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                  ) : (
                    <ArrowRightIcon className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );

  // Paso 2: Selección de vehículo
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">¿Qué vehículo vas a traer?</h2>
        <p className="text-gray-600">Selecciona tu vehículo o agrega uno nuevo</p>
      </div>

      {/* Servicios seleccionados */}
      {bookingData.services.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg space-y-2">
          <h3 className="font-medium text-blue-900 mb-2">Servicios seleccionados:</h3>
          {bookingData.services.map(svc => (
            <div key={svc.id} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <WrenchScrewdriverIcon className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">{svc.name}</span>
              </div>
              <span className="text-sm text-blue-700">${svc.price} • {svc.estimatedTime}</span>
            </div>
          ))}
        </div>
      )}

      {/* Selector de vehículo */}
      <VehicleManager
        showSelectOnly={true}
        selectedVehicleId={bookingData.vehicle?.id}
        onVehicleSelect={handleVehicleSelect}
      />

      {/* Botones de navegación */}
      <div className="flex justify-between pt-6">
        <button
          onClick={() => setCurrentStep(1)}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          ← Volver
        </button>
        <button
          onClick={() => setCurrentStep(3)}
          disabled={!canProceedToStep3()}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600"
        >
          Continuar →
        </button>
      </div>
    </div>
  );

  // Paso 3: Fecha y hora
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">¿Cuándo prefieres?</h2>
        <p className="text-gray-600">Selecciona la fecha y hora que mejor te convenga</p>
      </div>

      {/* Servicios seleccionados */}
      {bookingData.services.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg space-y-2">
          {bookingData.services.map(svc => (
            <div key={svc.id} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <WrenchScrewdriverIcon className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">{svc.name}</span>
              </div>
              <span className="text-sm text-blue-700">${svc.price} • {svc.estimatedTime}</span>
            </div>
          ))}
        </div>
      )}

      {/* Calendario */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <CalendarIcon className="w-5 h-5 inline mr-2" />
          Selecciona una fecha
        </label>
        <HolidayCalendar
          selectedDate={bookingData.date}
          onDateSelect={handleDateSelect}
          minDate={new Date().toISOString().split('T')[0]}
          className="max-w-md mx-auto"
        />
      </div>

      {/* Horarios */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <ClockIcon className="w-5 h-5 inline mr-2" />
          Selecciona una hora
          {isLoadingTimeSlots && (
            <span className="text-sm text-gray-500 ml-2">
              (Cargando horarios...)
            </span>
          )}
        </label>

        {bookingData.date ? (
          isLoadingTimeSlots ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p className="text-gray-500">Cargando horarios disponibles...</p>
            </div>
          ) : availableTimeSlots.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {availableTimeSlots.map(timeSlot => (
                <button
                  key={timeSlot.time}
                  onClick={() => handleTimeSelect(timeSlot.time)}
                  className={`p-3 text-center rounded-lg border transition-colors ${
                    bookingData.time === timeSlot.time
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <div className="font-medium">{formatTimeTo12Hour(timeSlot.time)}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {timeSlot.remainingSlots}/{timeSlot.maxCapacity} cupos
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                No hay horarios disponibles para esta fecha. Por favor selecciona otra fecha.
              </p>
            </div>
          )
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-500 text-sm">
              Selecciona una fecha primero para ver los horarios disponibles.
            </p>
          </div>
        )}
      </div>

      {/* Botones de navegación */}
      <div className="flex justify-between pt-6">
        <button
          onClick={() => setCurrentStep(2)}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          ← Volver
        </button>
        <button
          onClick={() => setCurrentStep(4)}
          disabled={!canProceedToStep4()}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600"
        >
          Continuar →
        </button>
      </div>
    </div>
  );

  // Paso 4: Confirmación
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirma tu cita</h2>
        <p className="text-gray-600">Revisa los detalles antes de confirmar</p>
      </div>

      {/* Resumen de la cita */}
      <div className="bg-gray-50 p-6 rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Servicios:</span>
          <span className="font-semibold">{bookingData.services.map(s => s.name).join(', ')}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Fecha:</span>
          <span className="font-semibold">
            {bookingData.date && new Date(bookingData.date + 'T00:00:00').toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              timeZone: 'America/Santo_Domingo'
            })}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Hora:</span>
          <span className="font-semibold">
            {bookingData.time ? formatTimeTo12Hour(bookingData.time) : 'No seleccionada'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Vehículo:</span>
          <span className="font-semibold">
            {bookingData.vehicle ? `${bookingData.vehicle.brand} ${bookingData.vehicle.model}` : 'No seleccionado'}
          </span>
        </div>
        <div className="flex items-center justify-between border-t pt-4">
          <span className="text-gray-600">Total:</span>
          <span className="text-xl font-bold text-blue-600">${bookingData.services.reduce((sum, s) => sum + (s.price || 0), 0)}</span>
        </div>
      </div>

      {/* Notas adicionales */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notas adicionales (opcional)
        </label>
        <textarea
          value={bookingData.notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          placeholder="Describe cualquier detalle especial o problema específico..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Botones de navegación */}
      <div className="flex justify-between pt-6">
        <button
          onClick={() => setCurrentStep(3)}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          ← Volver
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit() || isSubmitting}
          className="px-6 py-2 bg-green-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-600 flex items-center"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Confirmando...
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-5 h-5 mr-2" />
              Confirmar Cita
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Paso 5: Confirmación exitosa
  const renderStep5 = () => (
    <div className="text-center space-y-6">
      <CheckCircleIcon className="w-20 h-20 text-green-500 mx-auto" />
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Cita Confirmada!</h2>
        <p className="text-gray-600 mb-6">
          Tu cita con múltiples servicios ha sido programada exitosamente. Recibirás una confirmación por email.
        </p>
      </div>

      {/* Resumen final */}
      <div className="bg-gray-50 p-6 rounded-lg max-w-md mx-auto">
        <h3 className="font-semibold text-gray-900 mb-4">Resumen de tu cita</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Servicios:</span>
            <span className="font-medium">{bookingData.services.map(s => s.name).join(', ')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Fecha:</span>
            <span className="font-medium">
              {bookingData.date && new Date(bookingData.date + 'T00:00:00').toLocaleDateString('es-ES', {
                timeZone: 'America/Santo_Domingo'
              })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Hora:</span>
            <span className="font-medium">
              {bookingData.time ? formatTimeTo12Hour(bookingData.time) : 'No seleccionada'}
            </span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="text-gray-600">Total:</span>
            <span className="font-bold text-blue-600">${bookingData.services.reduce((sum, s) => sum + (s.price || 0), 0)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => navigate('/appointments')}
          className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Ver Mis Citas
        </button>
        <button
          onClick={() => {
            setCurrentStep(1);
            setBookingData({
              services: [],
              date: '',
              time: '',
              vehicle: null,
              notes: ''
            });
          }}
          className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Reservar Otra Cita
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Barra de progreso móvil fija arriba */}
      <div className="sticky top-0 z-50 md:hidden">
        <div className="bg-gray-50/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60">
          <div className="h-1.5 w-full bg-gray-200">
            <div
              className="h-1.5 bg-blue-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reservar Cita</h1>
          <p className="text-gray-600">Proceso rápido y sencillo en solo 5 pasos</p>
        </div>

        {/* Indicador de pasos (solo en pantallas medianas y grandes) */}
        <div className="hidden md:block">
          {renderStepIndicator()}
        </div>

        {/* Contenido del paso actual */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>
      </div>

      {/* Botón flotante para volver al inicio (solo en paso 1) */}
      {currentStep === 1 && (
        <div className="fixed bottom-6 left-6 z-50">
          <button
            onClick={() => navigate('/')}
            className="w-16 h-16 bg-gray-600 text-white rounded-full shadow-xl hover:bg-gray-700 transform hover:scale-110 transition-all duration-200 flex items-center justify-center border-4 border-white"
            style={{ boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)' }}
          >
            <ArrowLeftIcon className="w-8 h-8" />
          </button>
        </div>
      )}

      {/* Botón flotante para continuar desde el paso 1 */}
      {currentStep === 1 && bookingData.services.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={() => setCurrentStep(2)}
            className="px-6 py-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
          >
            <span>Continuar ({bookingData.services.length} servicio{bookingData.services.length !== 1 ? 's' : ''})</span>
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Botón flotante para retroceder entre pasos */}
      {currentStep > 1 && currentStep < 5 && (
        <div className="fixed bottom-6 left-6 z-50">
          <button
            onClick={() => setCurrentStep(currentStep - 1)}
            className="w-16 h-16 bg-blue-600 text-white rounded-full shadow-xl hover:bg-blue-700 transform hover:scale-110 transition-all duration-200 flex items-center justify-center border-4 border-white"
            style={{ boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)' }}
          >
            <ArrowLeftIcon className="w-8 h-8" />
          </button>
        </div>
      )}

      {/* Notificación */}
      <Notification
        type={notification.type}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
};

export default QuickBookPage;
